import React, { createContext, useState, useContext, useEffect } from "react";
import { auth, db } from "../firebase/config";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Liste des rôles valides
const VALID_ROLES = [
  'client', 
  'admin', 
  'projectmanager', 
  'charge-affaire',
  'superadmin',
  'hyperadmin'
];
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Vérifie d'abord dans la collection users
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            
            // Vérification et correction du rôle si nécessaire
            if (!VALID_ROLES.includes(userData.role)) {
              console.error(`Rôle inconnu : ${userData.role}`);
              userData.role = 'client';
            }
            
            console.log("Utilisateur trouvé dans 'users':", userData);
            setUser({ ...currentUser, ...userData, id: currentUser.uid });
          } else {
            // Si l'utilisateur n'est pas dans users, vérifie dans clientUsers
            const clientDocRef = doc(db, "clientUsers", currentUser.uid);
            const clientDocSnap = await getDoc(clientDocRef);
            
            if (clientDocSnap.exists()) {
              const clientData = clientDocSnap.data();
              
              // Assurer que le rôle est défini pour les clients
              if (!clientData.role) {
                clientData.role = 'client';
              }
              
              console.log("Client trouvé dans 'clientUsers':", clientData);
              setUser({ ...currentUser, ...clientData, id: currentUser.uid });
            } else {
              // Si l'utilisateur n'est dans aucune collection, utiliser uniquement les données de base
              console.log("Utilisateur non trouvé dans les collections. Données de base uniquement.");
              setUser({ ...currentUser, role: 'unknown', id: currentUser.uid });
            }
          }
        } catch (error) {
          console.error("Erreur lors de la récupération de l'utilisateur :", error);
          setUser({ ...currentUser, role: 'unknown', id: currentUser.uid });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    
    return unsubscribe;
  }, []);

  const signup = async (email, password, userData) => {
    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Validation du rôle lors de la création
      const role = VALID_ROLES.includes(userData.role) 
        ? userData.role 
        : 'client';
      
      await setDoc(doc(db, "users", newUser.uid), {
        ...userData,
        role,
        email,
        createdAt: new Date().toISOString(),
      });
      
      return newUser;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  // Fonction utilitaire pour obtenir le rôle d'un utilisateur
  const getUserRole = () => {
    return user?.role || 'unknown';
  };

  // Fonction pour vérifier si un utilisateur a un rôle spécifique
  const hasRole = (role) => {
    const userRole = getUserRole();
    return role === userRole;
  };

  // Fonction pour vérifier si un utilisateur a l'un des rôles spécifiés
  const hasAnyRole = (roles) => {
    const userRole = getUserRole();
    return roles.includes(userRole);
  };

  const value = { 
    user, 
    loading, 
    signup, 
    login, 
    logout,
    getUserRole,
    hasRole,
    hasAnyRole
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;